import { MbscModule } from '@mobiscroll/angular';
import { FormsModule } from '@angular/forms';
import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { AppComponent } from './app.component';
import { SharedModule } from '@deltager/organizers/admin/shared/shared.module';

import { AppMenuModule } from '@deltager/organizers/admin/core/app-menu/app-menu.module';

import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

import { ToastrModule } from 'ngx-toastr';
import { AppSettingsService } from '@deltager/organizers/admin/core/app-settings/app-settings.service';
import { RxHttpLoader } from '@deltager/organizers/admin/core/translations/rx-http-translate-loader';

import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaginatorIntlService } from '@deltager/organizers/admin/core/services/custom-paginator.service';
import { environment } from '@deltager/organizers/admin/environments/environment';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApiInterceptor } from '@deltager/organizers/admin/core/services/api-interceptor';

import { AppRoutingModule } from '@deltager/organizers/admin/core/app-routing/app-routing.module';
import { AffiliateService } from '@deltager/organizers/admin/core/services/affiliate.service';
import { AuthService } from '@deltager/organizers/admin/core/auth/auth.service';

import { AffiliateStartupService, AffiliateSettingsFactory } from './core/services/affiliate-startup.service';
import { ExpandMode, NgxTreeSelectModule } from './shared/components/tree-select';
import { BookingEventSetupEffects } from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.effects';
import { bookingEventSetupReducer } from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.reducer';
import { BookingEventSetupUIEffects } from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.ui.effects';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import {
  configServiceInitializerFactory,
  ConfigurationService,
} from '@deltager/organizers/admin/shared/configuration/configService';
import { COUNTRY_CODE, CULTURE_CODE } from '@deltager/organizers/admin/app.tokens';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  CUSTOM_DATE_FORMATS_DAYJS,
  CustomDatePickerAdapter,
} from '@deltager/organizers/admin/shared/custom-date-picker-adapter';
import { CultureCode } from './core/translations/culture-code';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { EventSetupEffects } from './modules/events/create-event/store/event-setup.effects';
import { eventSetupReducer } from './modules/events/create-event/store/event-setup.reducer';
import { EventSetupUIEffects } from './modules/events/create-event/store/event-setup.ui.effects';
import { activeImageSourcesReducer, editContentModeReducer, organizerReducer } from './redux/root.state';
import { SessionsEffects } from '@deltager/organizers/admin/modules/event-sessions/store/sessions.effects';
import { bookingReportReducer } from './modules/booking-report/store/booking-report.reducer';
import { BookingReportEffects } from './modules/booking-report/store/booking-report.effects';
import { dntEventsListReducer } from '@deltager/organizers/admin/modules/events/events-list-main/dnt-events-list/store/dnt-events-list.reducer';
import { DntEventsListEffects } from '@deltager/organizers/admin/modules/events/events-list-main/dnt-events-list/store/dnt-events-list.effects';
import { reportingReducer } from './modules/reporting/reporting-main/store/reporting.reducer';
import { ErrorInterceptor } from '@deltager/organizers/admin/shared/error-handler/http-error-interceptor';
import { WINDOW_PROVIDERS } from './core/window/window.service';
import { AppInitializerTranslateFactory } from './core/translations/initializer-translate.factory';
import { InitErrorOverlayComponent } from '@deltager/organizers/admin/shared/components/init-error-overlay/init-error-overlay.component';

export class TranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return params.key;
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    MbscModule,
    FormsModule,
    InitErrorOverlayComponent,
    BrowserAnimationsModule,
    HttpClientModule,
    NgxTreeSelectModule.forRoot({
      allowFilter: true,
      maxVisibleItemCount: 5,
      allowParentSelection: true,
      expandMode: ExpandMode.Selection,
    }),
    SharedModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: RxHttpLoader,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: TranslationHandler,
      },
      useDefaultLang: false,
    }),
    AppMenuModule,
    ToastrModule.forRoot(),
    StoreModule.forRoot(
      {
        event: eventSetupReducer,
        booking: bookingEventSetupReducer,
        activeImageSources: activeImageSourcesReducer,
        organizer: organizerReducer,
        isEditableContentMode: editContentModeReducer,
        bookingReport: bookingReportReducer,
        dntEventsList: dntEventsListReducer,
        reporting: reportingReducer,
      },
      {
        // can be uncomment for debugging
        // runtimeChecks: {
        //   strictStateSerializability: true,
        //   strictActionSerializability: true,
        // },
      },
    ),
    EffectsModule.forRoot([
      EventSetupEffects,
      SessionsEffects,
      EventSetupUIEffects,
      BookingEventSetupEffects,
      BookingEventSetupUIEffects,
      BookingReportEffects,
      DntEventsListEffects,
    ]),
    ...(environment.production ? [] : [StoreDevtoolsModule.instrument({ maxAge: 25 })]),
    AppRoutingModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  providers: [
    provideHttpClient(withInterceptors([ApiInterceptor])),
    ...WINDOW_PROVIDERS,
    RxHttpLoader,
    AppSettingsService,
    AuthService,
    AffiliateService,
    CookieService,
    {
      provide: APP_INITIALIZER,
      useFactory: configServiceInitializerFactory,
      deps: [ConfigurationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: AffiliateSettingsFactory,
      deps: [AffiliateStartupService, AppSettingsService, Injector],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: AppInitializerTranslateFactory,
      deps: [TranslateService, Injector, AppSettingsService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useFactory: translate => {
        const service = new PaginatorIntlService();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService],
    },
    {
      provide: 'environment',
      useValue: environment,
    },
    {
      provide: CULTURE_CODE,
      useFactory: (affiliateService: AffiliateService) => affiliateService.settings.cultureCode,
      deps: [AffiliateService],
    },
    {
      provide: LOCALE_ID,
      useClass: CultureCode,
    },
    {
      provide: COUNTRY_CODE,
      useFactory: (affiliateService: AffiliateService) => affiliateService.countryCode,
      deps: [AffiliateService],
    },
    { provide: DateAdapter, useClass: CustomDatePickerAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS_DAYJS },
    provideNgxMask(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
